import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../../supabase';
import Modal from '../../../../components/Modal';
import CorFormFields from '../CorFormFields';
import { badge } from '../../badges';
import {
  Cor,
  CorFormData,
  CorFormErrors,
  emptyCorForm,
  validateCor,
  corColumnValues,
} from '../../config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface CorDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  cor: Cor | null;
  onCorUpdated: () => void;
  onCorDeleted: () => void;
  teamMembers: TeamMember[];
  /** QA accounts can view but not edit (managers only). */
  readOnly?: boolean;
}

const corToForm = (cor: Cor): CorFormData => ({
  ...emptyCorForm(),
  type: cor.type,
  title: cor.title || '',
  raised_by: cor.raised_by?.id != null ? cor.raised_by.id.toString() : '',
  date_raised: cor.date_raised || '',
  description: cor.description || '',
  opportunity_component: cor.opportunity_component || '',
  risk_component: cor.risk_component || '',
  likelihood: cor.likelihood != null ? cor.likelihood.toString() : '',
  consequence: cor.consequence != null ? cor.consequence.toString() : '',
  management_approach: cor.management_approach || '',
  controls_actions: cor.controls_actions || '',
  owner: cor.owner?.id != null ? cor.owner.id.toString() : '',
  target_date: cor.target_date || '',
  residual_likelihood: cor.residual_likelihood != null ? cor.residual_likelihood.toString() : '',
  residual_consequence: cor.residual_consequence != null ? cor.residual_consequence.toString() : '',
  status: cor.status,
  closure_notes: cor.closure_notes || '',
});

const CorDetailModal: React.FC<CorDetailModalProps> = ({
  isOpen,
  onClose,
  cor,
  onCorUpdated,
  onCorDeleted,
  teamMembers,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState<CorFormData | null>(null);
  const [errors, setErrors] = useState<CorFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cor && isOpen) {
      setFormData(corToForm(cor));
      setErrors({});
      setError(null);
      setConfirmDelete(false);
    }
  }, [cor, isOpen]);

  const handleChange = useCallback(<K extends keyof CorFormData>(field: K, value: CorFormData[K]) => {
    setFormData(prev => (prev ? { ...prev, [field]: value } : prev));
    setErrors(prev => (prev[field as keyof CorFormErrors] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleSave = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cor || !formData) return;

    const newErrors = validateCor(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please complete the required fields before saving.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('cor_items')
        .update(corColumnValues(formData))
        .eq('id', cor.id);

      if (updateError) throw updateError;
      onCorUpdated();
    } catch (err) {
      console.error('Error updating COR item:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  }, [cor, formData, onCorUpdated]);

  const handleDelete = useCallback(async () => {
    if (!cor) return;
    setIsDeleting(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('cor_items')
        .delete()
        .eq('id', cor.id);
      if (deleteError) throw deleteError;
      onCorDeleted();
    } catch (err) {
      console.error('Error deleting COR item:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while deleting');
      setIsDeleting(false);
    }
  }, [cor, onCorDeleted]);

  if (!cor || !formData) return null;

  const headerStatus = badge(formData.status);

  const modalFooter = readOnly ? (
    <div className="modal-footer-actions">
      <span className="field-hint" style={{ marginRight: 'auto' }}>View only — managers can edit.</span>
      <button type="button" onClick={onClose} className="btn-secondary">Close</button>
    </div>
  ) : (
    <div className="modal-footer-actions">
      {confirmDelete ? (
        <div className="delete-confirm">
          <span>Delete this item?</span>
          <button type="button" onClick={() => setConfirmDelete(false)} className="btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleDelete} disabled={isDeleting} className="btn-danger">
            {isDeleting ? 'Deleting...' : 'Confirm Delete'}
          </button>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => setConfirmDelete(true)} className="btn-danger">
            Delete
          </button>
          <button type="submit" form="cor-detail-form" disabled={isSaving} className="btn-primary">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={cor.reference}
      size="md"
      className="cor-modal"
      hasFooter={true}
      footerContent={modalFooter}
      headerStatus={headerStatus}
    >
      <div className="form-content">
        <form id="cor-detail-form" onSubmit={handleSave}>
          {error && <div className="error-message general-error">{error}</div>}
          <CorFormFields
            formData={formData}
            onChange={handleChange}
            teamMembers={teamMembers}
            errors={errors}
            required
            disabled={readOnly}
          />
        </form>
      </div>
    </Modal>
  );
};

export default CorDetailModal;
